// Settlement Planner Page
// BP-10-04: Attorney Frontend

import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, FileText } from 'lucide-react';
import { useCase, useLifeCarePlans, useSaveSettlement } from '@/features/attorney/hooks/useAttorney';
import { SettlementCalculator, CaseStatusBadge } from '../components';
import type { CaseStatus } from '@/features/attorney/types/attorney';

export function SettlementPlannerPage() {
  const { caseId } = useParams<{ caseId: string }>();
  
  const { data: caseData } = useCase(caseId!);
  const { data: lifeCarePlans } = useLifeCarePlans(caseId!);
  const saveMutation = useSaveSettlement(caseId!);

  // Get the total lifetime cost from the most recent life care plan
  const latestPlan = lifeCarePlans?.[0];
  const lifetimeCostEstimate = latestPlan?.totalLifetimeCost || 0;

  const handleSaveSettlement = async (data: any) => {
    await saveMutation.mutateAsync(data);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link
            to={`/attorney/cases/${caseId}`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-1"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-semibold text-navy">Settlement Planner</h1>
              {caseData && (
                <CaseStatusBadge status={caseData.status as CaseStatus} />
              )}
            </div>
            <p className="text-gray-600">
              {caseData?.family?.name || 'Case'} • Distribution & structured settlement modeling
            </p>
          </div>
        </div>
      </div>

      {/* Life Care Plan Reference */}
      {latestPlan ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-green-800">
                Life Care Plan Available
              </h4>
              <p className="text-sm text-green-700 mt-1">
                Latest plan by {latestPlan.preparerName} estimates lifetime costs at{' '}
                <strong>
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                  }).format(lifetimeCostEstimate)}
                </strong>
              </p>
              <Link
                to={`/attorney/cases/${caseId}/life-care`}
                className="text-sm font-medium text-green-800 hover:text-green-900 mt-2 inline-block"
              >
                View Life Care Plan →
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">
                No Life Care Plan
              </h4>
              <p className="text-sm text-amber-700 mt-1">
                Create a life care plan to get lifetime cost estimates for settlement comparison.
              </p>
              <Link
                to={`/attorney/cases/${caseId}/life-care`}
                className="text-sm font-medium text-amber-800 hover:text-amber-900 mt-2 inline-block"
              >
                Create Life Care Plan →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Settlement Calculator */}
      <SettlementCalculator
        caseId={caseId!}
        lifetimeCostEstimate={lifetimeCostEstimate}
        onSave={handleSaveSettlement}
      />

      {/* Trust Planning Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-navy mb-4">
          Trust Structure Planning
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="border border-gray-200 rounded-lg p-4 hover:border-sage transition-colors cursor-pointer">
            <h4 className="font-medium text-navy mb-2">Special Needs Trust</h4>
            <p className="text-sm text-gray-600">
              Preserve government benefits eligibility while supplementing care.
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 hover:border-sage transition-colors cursor-pointer">
            <h4 className="font-medium text-navy mb-2">Pooled Trust</h4>
            <p className="text-sm text-gray-600">
              Managed by nonprofit organizations with pooled investment.
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 hover:border-sage transition-colors cursor-pointer">
            <h4 className="font-medium text-navy mb-2">ABLE Account</h4>
            <p className="text-sm text-gray-600">
              Tax-advantaged savings for disability-related expenses.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettlementPlannerPage;


